// APAI-2402: KaTeX is rendered with `output: 'html'`, which produces only the
// visual `.katex-html` markup (always `aria-hidden`). Screen readers therefore
// get nothing for math. This converts the LaTeX source into a linear plain-text
// form built from Unicode math symbols, which assistive tech verbalises in the
// user's own language.

const SYMBOLS: Record<string, string> = {
  times: '×',
  div: '÷',
  cdot: '·',
  cdotp: '·',
  ast: '*',
  star: '*',
  pm: '±',
  mp: '∓',
  le: '≤',
  leq: '≤',
  ge: '≥',
  geq: '≥',
  ne: '≠',
  neq: '≠',
  ll: '≪',
  gg: '≫',
  approx: '≈',
  equiv: '≡',
  cong: '≅',
  sim: '∼',
  simeq: '≃',
  propto: '∝',
  to: '→',
  rightarrow: '→',
  longrightarrow: '→',
  Rightarrow: '⇒',
  leftarrow: '←',
  longleftarrow: '←',
  Leftarrow: '⇐',
  leftrightarrow: '↔',
  Leftrightarrow: '⇔',
  mapsto: '↦',
  infty: '∞',
  partial: '∂',
  nabla: '∇',
  prime: '′',
  sum: '∑',
  prod: '∏',
  int: '∫',
  iint: '∬',
  oint: '∮',
  in: '∈',
  notin: '∉',
  ni: '∋',
  subset: '⊂',
  subseteq: '⊆',
  supset: '⊃',
  supseteq: '⊇',
  cup: '∪',
  cap: '∩',
  setminus: '\\',
  emptyset: '∅',
  varnothing: '∅',
  forall: '∀',
  exists: '∃',
  nexists: '∄',
  neg: '¬',
  land: '∧',
  lor: '∨',
  angle: '∠',
  measuredangle: '∠',
  perp: '⊥',
  parallel: '∥',
  triangle: '△',
  square: '□',
  therefore: '∴',
  because: '∵',
  ldots: '…',
  dots: '…',
  cdots: '…',
  vdots: '⋮',
  ddots: '⋱',
  degree: '°',
  circ: '°',
  percent: '%',
  lbrace: '{',
  rbrace: '}',
  langle: '⟨',
  rangle: '⟩',
  lvert: '|',
  rvert: '|',
  vert: '|',
  lVert: '‖',
  rVert: '‖',
  Vert: '‖',
  alpha: 'α',
  beta: 'β',
  gamma: 'γ',
  delta: 'δ',
  epsilon: 'ε',
  varepsilon: 'ε',
  zeta: 'ζ',
  eta: 'η',
  theta: 'θ',
  vartheta: 'θ',
  iota: 'ι',
  kappa: 'κ',
  lambda: 'λ',
  mu: 'μ',
  nu: 'ν',
  xi: 'ξ',
  pi: 'π',
  varpi: 'π',
  rho: 'ρ',
  varrho: 'ρ',
  sigma: 'σ',
  varsigma: 'ς',
  tau: 'τ',
  upsilon: 'υ',
  phi: 'φ',
  varphi: 'φ',
  chi: 'χ',
  psi: 'ψ',
  omega: 'ω',
  Gamma: 'Γ',
  Delta: 'Δ',
  Theta: 'Θ',
  Lambda: 'Λ',
  Xi: 'Ξ',
  Pi: 'Π',
  Sigma: 'Σ',
  Upsilon: 'Υ',
  Phi: 'Φ',
  Psi: 'Ψ',
  Omega: 'Ω',
};

const SUPERSCRIPTS: Record<string, string> = {
  '0': '⁰',
  '1': '¹',
  '2': '²',
  '3': '³',
  '4': '⁴',
  '5': '⁵',
  '6': '⁶',
  '7': '⁷',
  '8': '⁸',
  '9': '⁹',
  '+': '⁺',
  '-': '⁻',
  '=': '⁼',
  '(': '⁽',
  ')': '⁾',
  n: 'ⁿ',
  i: 'ⁱ',
};

const SUBSCRIPTS: Record<string, string> = {
  '0': '₀',
  '1': '₁',
  '2': '₂',
  '3': '₃',
  '4': '₄',
  '5': '₅',
  '6': '₆',
  '7': '₇',
  '8': '₈',
  '9': '₉',
  '+': '₊',
  '-': '₋',
  '=': '₌',
  '(': '₍',
  ')': '₎',
  a: 'ₐ',
  n: 'ₙ',
  i: 'ᵢ',
  x: 'ₓ',
};

const FUNCTIONS = new Set([
  'sin',
  'cos',
  'tan',
  'cot',
  'sec',
  'csc',
  'arcsin',
  'arccos',
  'arctan',
  'sinh',
  'cosh',
  'tanh',
  'log',
  'ln',
  'lg',
  'exp',
  'lim',
  'limsup',
  'liminf',
  'max',
  'min',
  'sup',
  'inf',
  'det',
  'gcd',
  'deg',
  'dim',
  'arg',
  'Pr',
  'mod',
  'bmod',
  'pmod',
]);

// Commands whose only meaning is styling or annotation: keep the argument.
const WRAPPER_COMMANDS = new Set([
  'text',
  'textrm',
  'textbf',
  'textit',
  'texttt',
  'textsf',
  'textnormal',
  'mathrm',
  'mathbf',
  'mathit',
  'mathtt',
  'mathsf',
  'mathbb',
  'mathcal',
  'mathfrak',
  'mathnormal',
  'boldsymbol',
  'bm',
  'operatorname',
  'mbox',
  'hbox',
  'underline',
  'overline',
  'bar',
  'hat',
  'widehat',
  'tilde',
  'widetilde',
  'vec',
  'dot',
  'ddot',
  'boxed',
  'mathop',
  'mathbin',
  'mathrel',
  'mathopen',
  'mathclose',
]);

// Commands with no spoken equivalent and no argument to keep.
const IGNORED_COMMANDS = new Set([
  'big',
  'Big',
  'bigg',
  'Bigg',
  'bigl',
  'bigr',
  'Bigl',
  'Bigr',
  'biggl',
  'biggr',
  'Biggl',
  'Biggr',
  'middle',
  'displaystyle',
  'textstyle',
  'scriptstyle',
  'scriptscriptstyle',
  'limits',
  'nolimits',
  'strut',
  'mathstrut',
  'notag',
  'nonumber',
]);

// Commands whose argument is layout-only and must be dropped with them.
const DISCARDED_ARG_COMMANDS = new Set([
  'phantom',
  'hphantom',
  'vphantom',
  'hspace',
  'vspace',
  'raisebox',
  'label',
  'tag',
  'rule',
  'kern',
  'mkern',
]);

const SPACE_COMMANDS = new Set([',', ';', ':', ' ', 'quad', 'qquad', 'thinspace', 'enspace']);

const ESCAPED_CHARS = new Set(['{', '}', '%', '$', '&', '#', '_']);

const ARGUMENT_COUNTS: Record<string, number> = {
  frac: 2,
  dfrac: 2,
  tfrac: 2,
  cfrac: 2,
  textcolor: 2,
  colorbox: 2,
  sqrt: 1,
  color: 1,
};

function argumentCount(name: string): number {
  if (Object.prototype.hasOwnProperty.call(ARGUMENT_COUNTS, name)) return ARGUMENT_COUNTS[name];

  return WRAPPER_COMMANDS.has(name) || DISCARDED_ARG_COMMANDS.has(name) ? 1 : 0;
}

function readGroup(latex: string, index: number): [string, number] {
  let depth = 0;

  for (let i = index; i < latex.length; i++) {
    const char = latex[i];

    if (char === '\\') {
      i++;
    } else if (char === '{') {
      depth++;
    } else if (char === '}') {
      depth--;
      if (depth === 0) return [latex.slice(index + 1, i), i + 1];
    }
  }

  return [latex.slice(index + 1), latex.length];
}

function readArgument(latex: string, index: number): [string, number] {
  let i = index;
  while (i < latex.length && /\s/.test(latex[i])) i++;

  if (i >= latex.length) return ['', i];
  if (latex[i] === '{') return readGroup(latex, i);

  if (latex[i] === '\\') {
    const match = /^\\([a-zA-Z]+|[\s\S])/.exec(latex.slice(i));

    if (match) {
      let end = i + match[0].length;

      if (match[1] === 'sqrt' && latex[end] === '[') {
        const close = latex.indexOf(']', end);
        if (close !== -1) end = close + 1;
      }

      // An unbraced argument can itself be a command taking arguments, as in
      // `\sqrt\frac{1}{2}`, and they belong to it rather than to the caller.
      for (let pending = argumentCount(match[1]); pending > 0; pending--) {
        end = readArgument(latex, end)[1];
      }

      return [latex.slice(i, end), end];
    }
  }

  return [latex[i], i + 1];
}

function toScript(text: string, map: Record<string, string>): string | null {
  if (!text) return null;

  let out = '';
  for (let i = 0; i < text.length; i++) {
    const mapped = map[text[i]];
    if (!mapped) return null;
    out += mapped;
  }

  return out;
}

// A multi-token operand needs parentheses so "a+b over c" cannot be misheard.
function wrapOperand(text: string): string {
  const trimmed = text.trim();
  if (!trimmed) return '';

  // ²³¹ and ⁰-₟ are the Unicode super/subscripts
  return /^[\w.°²³¹⁰-₟]+$/.test(trimmed) ? trimmed : `(${trimmed})`;
}

function convert(latex: string): string {
  let out = '';
  let i = 0;

  while (i < latex.length) {
    const char = latex[i];

    if (char === '\\') {
      const match = /^\\([a-zA-Z]+|[\s\S])/.exec(latex.slice(i));
      if (!match) {
        i++;
        continue;
      }

      const name = match[1];
      i += match[0].length;

      if (name === '\\') {
        out += '; ';
      } else if (name === '!') {
        // negative thin space: nothing to say
      } else if (SPACE_COMMANDS.has(name)) {
        out += ' ';
      } else if (ESCAPED_CHARS.has(name)) {
        out += name;
      } else if (name === 'frac' || name === 'dfrac' || name === 'tfrac' || name === 'cfrac') {
        const [numerator, afterNumerator] = readArgument(latex, i);
        const [denominator, afterDenominator] = readArgument(latex, afterNumerator);
        i = afterDenominator;
        out += `${wrapOperand(convert(numerator))}/${wrapOperand(convert(denominator))}`;
      } else if (name === 'sqrt') {
        let cursor = i;
        let degree = '';
        while (cursor < latex.length && /\s/.test(latex[cursor])) cursor++;

        if (latex[cursor] === '[') {
          const close = latex.indexOf(']', cursor);
          if (close !== -1) {
            degree = convert(latex.slice(cursor + 1, close)).trim();
            cursor = close + 1;
          }
        }

        const [radicand, afterRadicand] = readArgument(latex, cursor);
        i = afterRadicand;
        const body = wrapOperand(convert(radicand));

        if (degree === '3') out += `∛${body}`;
        else if (degree === '4') out += `∜${body}`;
        else if (degree) out += `√[${degree}]${body}`;
        else out += `√${body}`;
      } else if (name === 'left' || name === 'right') {
        let cursor = i;
        while (cursor < latex.length && /\s/.test(latex[cursor])) cursor++;
        // `\left.` / `\right.` are invisible delimiters
        if (latex[cursor] === '.') i = cursor + 1;
      } else if (name === 'textcolor' || name === 'colorbox') {
        const [, afterColor] = readArgument(latex, i);
        const [content, afterContent] = readArgument(latex, afterColor);
        i = afterContent;
        out += convert(content);
      } else if (name === 'color' || DISCARDED_ARG_COMMANDS.has(name)) {
        const [, next] = readArgument(latex, i);
        i = next;
      } else if (WRAPPER_COMMANDS.has(name)) {
        const [content, next] = readArgument(latex, i);
        i = next;
        out += convert(content);
      } else if (name === 'begin' || name === 'end') {
        const [environment, next] = readArgument(latex, i);
        i = next;
        const environmentName = environment.trim().replace(/\*$/, '');

        if (name === 'begin' && (environmentName === 'array' || environmentName === 'tabular')) {
          let cursor = i;
          while (cursor < latex.length && /\s/.test(latex[cursor])) cursor++;
          if (latex[cursor] === '{') {
            const [, afterSpec] = readGroup(latex, cursor);
            i = afterSpec;
          }
        }

        out += ' ';
      } else if (IGNORED_COMMANDS.has(name)) {
        // nothing to say
      } else if (FUNCTIONS.has(name)) {
        out += ` ${name}`;
        // keep "log₂8" and "sin(x)" tight, but separate "sin θ"
        if (latex[i] && !'_^('.includes(latex[i])) out += ' ';
      } else if (typeof SYMBOLS[name] === 'string') {
        // typeof, not truthiness: `\constructor` would otherwise resolve
        // against Object.prototype
        out += SYMBOLS[name];
      }

      continue;
    }

    if (char === '^' || char === '_') {
      const [argument, next] = readArgument(latex, i + 1);
      i = next;
      const inner = convert(argument).trim();

      if (inner === '°' || inner === '′') {
        out += inner;
        continue;
      }

      const scripted = toScript(inner, char === '^' ? SUPERSCRIPTS : SUBSCRIPTS);
      out += scripted ?? (inner ? `${char}(${inner})` : '');
      continue;
    }

    if (char === '{' || char === '}' || char === '$') {
      i++;
      continue;
    }

    if (char === '&') {
      out += ' ';
      i++;
      continue;
    }

    out += char;
    i++;
  }

  return out;
}

export function latexToText(latex: string | null | undefined): string {
  if (!latex) return '';

  return convert(latex).replace(/\s+/g, ' ').trim();
}

export default latexToText;
